import React, { forwardRef } from "react";
import { Dumbbell, Trophy } from "lucide-react";
import RankBadge from "./RankBadge";
import { fitTitleWithWrap, fitTextSize } from "../lib/fitText";

/**
 * 1080×1080 shareable rank card. Rendered off-screen and converted to PNG via html-to-image.
 * Stays inside our brand: Deep Obsidian + Electric Blaze accent + Barlow Condensed display font.
 */
const RankShareCard = forwardRef(function RankShareCard({ rank, totalExp, handle }, ref) {
    if (!rank) return null;
    // Card padding 64 each side + 140px rank badge + 36px gap + 8px badge pad
    // × 2 → available width for the rank name column is ~760px. Rank names
    // range from "Iron Pup" (8 chars) to "Titan of the Iron Age" (21 chars);
    // auto-fit + wrap if needed. Tagline gets its own smaller autofit.
    const title = fitTitleWithWrap(rank.name || "", {
        maxWidth: 760,
        maxSize: 130,
        minSize: 62,
        wrapThreshold: 72,
    });
    const taglineSize = fitTextSize(rank.tagline || "", {
        maxWidth: 760,
        maxSize: 38,
        minSize: 26,
        avgCharRatio: 0.48, // italic mixed-case sits narrower than uppercase display
    });
    return (
        <div
            ref={ref}
            data-testid="rank-share-card"
            style={{
                width: 1080,
                height: 1080,
                background: "#0a0a0a",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
                fontFamily: '"Barlow Condensed", "DM Sans", system-ui, sans-serif',
            }}
        >
            {/* Diagonal stripe background */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(255,59,48,0.06) 0px, rgba(255,59,48,0.06) 2px, transparent 2px, transparent 28px)",
                }}
            />
            {/* Red glow */}
            <div
                style={{
                    position: "absolute",
                    left: -200,
                    top: -200,
                    width: 800,
                    height: 800,
                    background: "radial-gradient(circle, rgba(255,59,48,0.35) 0%, rgba(255,59,48,0) 60%)",
                    filter: "blur(8px)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    right: -240,
                    bottom: -260,
                    width: 800,
                    height: 800,
                    background: "radial-gradient(circle, rgba(255,59,48,0.25) 0%, rgba(255,59,48,0) 60%)",
                    filter: "blur(8px)",
                }}
            />

            {/* Top brand bar */}
            <div style={{ position: "absolute", top: 60, left: 64, right: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Dumbbell size={36} color="#ff3b30" />
                    <span style={{ fontWeight: 900, fontSize: 38, letterSpacing: -0.5, textTransform: "uppercase" }}>
                        Hyper<span style={{ color: "#ff3b30" }}>gains</span>
                    </span>
                </div>
                <span style={{ fontSize: 16, letterSpacing: 4, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
                    Rank #{String(rank.id).padStart(2, "0")} · {rank.tier} Tier
                </span>
            </div>

            {/* Center stack */}
            <div style={{ position: "absolute", left: 64, right: 64, top: 220, display: "flex", alignItems: "flex-start", gap: 36 }}>
                {/* Insignia */}
                <div style={{ flexShrink: 0, padding: 8, border: "2px solid rgba(255,59,48,0.4)", background: "rgba(255,59,48,0.05)" }}>
                    <RankBadge rankId={rank.id} size="lg" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, color: "#ff3b30", fontSize: 22, letterSpacing: 6, textTransform: "uppercase", fontWeight: 800 }}>
                        <Trophy size={36} color="#ff3b30" />
                        Rank Unlocked
                    </div>
                    <div style={{ marginTop: 24, fontWeight: 900, fontSize: title.size, lineHeight: 0.92, letterSpacing: -2, textTransform: "uppercase" }}>
                        {title.lines.map((line, i) => (
                            <div key={i}>{line}</div>
                        ))}
                    </div>
                    <div style={{ marginTop: 28, fontStyle: "italic", fontSize: taglineSize, lineHeight: 1.15, maxWidth: 780, color: "rgba(255,255,255,0.88)" }}>
                        "{rank.tagline}"
                    </div>
                </div>
            </div>

            {/* Stat strip */}
            <div style={{ position: "absolute", left: 64, right: 64, bottom: 160, display: "flex", gap: 24 }}>
                <div style={{ flex: 1, border: "2px solid rgba(255,255,255,0.12)", padding: "28px 32px", background: "rgba(255,255,255,0.03)" }}>
                    <div style={{ fontSize: 16, letterSpacing: 4, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", fontWeight: 700 }}>Total EXP</div>
                    <div style={{ marginTop: 10, fontWeight: 900, fontSize: 64, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {(totalExp ?? rank.min_exp).toLocaleString()}
                    </div>
                </div>
                <div style={{ flex: 1, border: "2px solid #ff3b30", padding: "28px 32px", background: "rgba(255,59,48,0.08)" }}>
                    <div style={{ fontSize: 16, letterSpacing: 4, color: "#ff3b30", textTransform: "uppercase", fontWeight: 700 }}>Threshold</div>
                    <div style={{ marginTop: 10, fontWeight: 900, fontSize: 64, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {rank.min_exp.toLocaleString()} EXP
                    </div>
                </div>
            </div>

            {/* Footer — explicit live URL so anyone reading the screenshot can type it in */}
            <div style={{ position: "absolute", left: 64, right: 64, bottom: 56, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 24 }}>
                <span style={{ fontSize: 22, letterSpacing: 6, textTransform: "uppercase", fontWeight: 800, color: "rgba(255,255,255,0.65)" }}>
                    {handle ? handle : "Earned, not given."}
                </span>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, letterSpacing: 5, textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontWeight: 700 }}>Find out where you stand</div>
                    <div style={{ marginTop: 6, fontSize: 30, letterSpacing: 2, color: "#ff3b30", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
                        hypergains.fit
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RankShareCard;
